import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { register, loginWithGoogle } from "../lib/firebase";
import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { FirebaseError } from "firebase/app";
import "./auth.css";

function authMessage(err: unknown): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "auth/email-already-in-use":
        return "An account already exists for this email. Try signing in.";
      case "auth/invalid-email":
        return "That doesn't look like a valid email.";
      case "auth/weak-password":
        return "Password is too weak — use at least 6 characters.";
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}

export function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleGoogle = async () => {
    setError(null);
    setBusy(true);
    try {
      await loginWithGoogle();
      navigate("/", { replace: true });
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError("Use a password of at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await register(email.trim(), password);
      // A verification email is sent automatically; route to the prompt.
      navigate("/verify-email", { replace: true });
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <img className="auth__logo" src="/aisg-logo.webp" alt="AI Singapore" />
          <div>
            <div className="auth__brand-text">LADP Scoping Assistant</div>
            <div className="auth__brand-sub">AI Singapore</div>
          </div>
        </div>

        <h1 className="auth__title">Create your account</h1>
        <p className="auth__lede">
          We'll email you a verification link before you start.
        </p>

        <form className="auth__form" onSubmit={handleSubmit}>
          {error && <div className="auth__error">{error}</div>}

          <Input
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Password"
            type="password"
            name="new-password"
            autoComplete="new-password"
            placeholder="At least 6 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Input
            label="Confirm password"
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            placeholder="Re-enter your password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />

          <Button type="submit" variant="primary" block loading={busy}>
            Create account
          </Button>
        </form>

        <div className="auth__divider"><span>or</span></div>

        <Button
          type="button"
          variant="secondary"
          block
          disabled={busy}
          onClick={handleGoogle}
        >
          Continue with Google
        </Button>

        <div className="auth__footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
